/**
 * STATIC MODE — the warroom with no server behind it (airsec.akeyo.io).
 *
 * The published site is GitHub Pages: files, nothing else. The warroom normally needs a
 * FastAPI process (one WebSocket carrying both lanes, plus REST for the past). Rather than
 * write a second, weaker warroom for the web, the static build REPLAYS a recorded session:
 * `scripts/record_session.py` captures the socket's own BYTES and bakes the REST answers,
 * and this module hands them back through the same entry points the live app uses. The
 * decoder, the interpolation, the wall, the follow logic — all of it runs the shipped code
 * on the shipped protocol. A bug cannot hide in a second implementation, because there
 * isn't one.
 *
 * What is honestly different, and said out loud in the UI badge: the world is a recording,
 * so it cannot answer a NEW question (a re-subscribe at another altitude, a history for
 * someone the recording never saw). Everything the operator does client-side — select,
 * mark red, follow-by-camera, scrub the wall, read a dossier that was baked — is live.
 */

const STATIC_HOSTS = new Set(['airsec.akeyo.io'])

/** The same rule the standalone preview uses, so one page cannot think it is live while
 *  the other thinks it is not. `?static` forces it on for local verification. */
export function isStatic(): boolean {
  if (typeof location === 'undefined') return false
  return STATIC_HOSTS.has(location.hostname)
    || location.hostname.endsWith('.github.io')
    || new URLSearchParams(location.search).has('static')
}

/** Where the baked bundle lives, relative to the warroom page itself. */
const ROOT = '../static'

/** Media clock of the recording's first frame — the videos are seeked against this. */
let mediaT0 = 0
let durationS = 300
/** ms into the replay, published by the replayer so `/demo/playhead` can answer. */
let replayMs = 0

export function setReplayClock(ms: number, t0: number, duration: number): void {
  replayMs = ms
  mediaT0 = t0
  durationS = duration
}

/** Map a live API path onto a file in the baked bundle. `null` = nothing to serve. */
export function staticUrlFor(path: string): string | null {
  const clean = path.split('?')[0]
  if (clean === '/demo/info') return `${ROOT}/api/demo-info.json`
  if (clean === '/snapshot') return `${ROOT}/api/snapshot.json`
  if (clean === '/demo/flags') return `${ROOT}/api/flags.json`
  if (clean === '/demo/patterns') return `${ROOT}/api/patterns.json`
  // The gid arrives ALREADY percent-encoded (the caller encodes it, and a gid carries a
  // `#`). Encoding it again turns `%23` into `%2523` and every dossier 404s — the file on
  // disk is named with the raw gid, which is exactly what the encoded url asks for.
  if (clean.startsWith('/history/person/')) {
    return `${ROOT}/api/history/${clean.slice('/history/person/'.length)}.json`
  }
  if (clean.startsWith('/history/item/')) {
    return `${ROOT}/api/history/${clean.slice('/history/item/'.length)}.json`
  }
  if (clean.startsWith('/boxtracks/')) return `${ROOT}/api/boxtracks/${clean.slice('/boxtracks/'.length)}`
  if (clean.startsWith('/derived/')) return `${ROOT}/api/derived/${clean.slice('/derived/'.length)}`
  if (clean.startsWith('/clips/')) return `${ROOT}/clips/${clean.slice('/clips/'.length)}`
  if (clean.startsWith('/data/')) return `${ROOT}/data/${clean.slice('/data/'.length)}`
  return null
}

/**
 * Install the rewriter. ONE interception point for the whole app: every panel keeps its
 * own honest URL (`/history/person/P-3`), and only this function knows the site is static.
 * Rewriting here rather than in each caller is what keeps the live and static builds the
 * same code — the alternative is a static-mode branch in every panel, which is how the two
 * builds drift apart and only one of them stays tested.
 */
export function installStaticNet(): void {
  if (!isStatic() || typeof window === 'undefined') return
  const real = window.fetch.bind(window)
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    // Only same-origin, absolute-path calls are ours to answer.
    if (url.startsWith('/')) {
      if (url.split('?')[0] === '/demo/playhead') {
        const body = JSON.stringify({
          rung: 'b',
          sim_t: mediaT0 + replayMs / 1000,
          media_time_s: (mediaT0 + replayMs / 1000) % durationS,
          duration_s: durationS,
          pack_fps: 30.0,
          loop: 0,
          static: true,
        })
        return Promise.resolve(new Response(body, { status: 200, headers: { 'content-type': 'application/json' } }))
      }
      if (url.split('?')[0] === '/ops/restart') {
        // No server to restart. Say so honestly rather than hanging the button.
        return Promise.resolve(new Response('{"restarting":false,"static":true}',
          { status: 200, headers: { 'content-type': 'application/json' } }))
      }
      const mapped = staticUrlFor(url)
      if (mapped) return real(mapped, init)
    }
    return real(input as RequestInfo, init)
  }) as typeof window.fetch
}

/**
 * Load the recorded socket stream: the raw bytes, plus an index of
 * `[offset_ms, byte_offset, length]` per frame. Kept as ONE blob so the browser makes one
 * request for a 20 MB session instead of thousands.
 */
export interface Recording {
  bytes: Uint8Array
  frames: [number, number, number][]
  msTotal: number
  mediaT0: number
  durationS: number
}

export async function loadRecording(): Promise<Recording> {
  const [idxRes, binRes, headRes] = await Promise.all([
    fetch(`${ROOT}/session/stream.idx.json`),
    fetch(`${ROOT}/session/stream.bin`),
    fetch(`${ROOT}/session/head.json`),
  ])
  if (!idxRes.ok || !binRes.ok) throw new Error('static session bundle missing')
  const idx = await idxRes.json() as { ms_total: number; frames: [number, number, number][] }
  const bytes = new Uint8Array(await binRes.arrayBuffer())
  const head = headRes.ok
    ? await headRes.json() as { media_time_s?: number; duration_s?: number }
    : {}
  return {
    bytes,
    frames: idx.frames,
    msTotal: idx.ms_total,
    mediaT0: head.media_time_s ?? 0,
    durationS: head.duration_s ?? 300,
  }
}
